import { FC, SyntheticEvent, useMemo, useRef, useState } from 'react';

import ELEMENT from 'common/element';
import { TOKEN_TYPE } from 'common/util/filter/types';
import { useFacetsNeeded } from 'hooks/useFacetsNeeded';

import ResizableTextArea from 'components/common/Input/ResizableTextArea';
import SearchHelper from 'components/search/SearchHelper/SearchHelper';

import './style.scss';

const PQ_INPUT_MIN_HEIGHT = '135px';

const PQ_INPUT_EXPAND_PROPS = {
  enabled: true,
  minHeight: PQ_INPUT_MIN_HEIGHT,
};

type PowerQueryInputProps = {
  value: string;
  onInputValueChange: (input: string) => void;
  onPressEnter: () => void;
};

/**
 * Resizable text area for Power Query Input
 *
 * @prop value                 text content of the input
 * @prop onInputValueChange    function to run when the value changes
 * @prop onPressEnter          function to run when Enter has been typed
 */
const PowerQueryInput: FC<PowerQueryInputProps> = ({
  value,
  onInputValueChange,
  onPressEnter,
}: PowerQueryInputProps) => {
  const [showSearchHelper, setShowSearchHelper] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useFacetsNeeded();

  const inputChangeHandler = (event: SyntheticEvent): void => {
    const target = event.target as HTMLTextAreaElement;
    onInputValueChange(target.value);

    // show the search helper if there is any filter text; the helper may not actually appear depending on the filter
    if (target.value) {
      setShowSearchHelper(true);
    }
  };

  const excludeTypes = useMemo(() => [TOKEN_TYPE.FUNCTION], []);

  return (
    <SearchHelper
      showSearchHelper={showSearchHelper}
      setShowSearchHelper={setShowSearchHelper}
      onChange={(filter: string): void => onInputValueChange(filter)}
      onEnter={onPressEnter}
      useShiftOnEnter
      useMetaOnEnter
      inputRef={inputRef}
      excludeTypes={excludeTypes}
    >
      <ResizableTextArea
        value={value}
        onChange={inputChangeHandler}
        placeholder="Enter adds new line. Cmd+Enter runs search. Use the pipe character | to add a command."
        minRows={4}
        labelClassName="PowerQueryInputLabel"
        inputClassName="PowerQueryInput"
        expandProps={PQ_INPUT_EXPAND_PROPS}
        useShiftOnEnter
        useMetaOnEnter
        focused
        testId={ELEMENT.powerQueryInput}
        inputRef={inputRef}
      />
    </SearchHelper>
  );
};

export default PowerQueryInput;
